/**
 * Krybot API Service
 * Provides methods for interacting with Krybot endpoints
 */

import { apiClient } from '../apiClient';
import type {
  KrybotAgent,
  KrybotAgentCreate,
  KrybotAgentUpdate,
  KrybotAgentVersion,
  KrybotAgentVersionCreate,
  KrybotAgentRun,
  KrybotAgentRunCreate,
  KrybotAgentRunStatus,
  KrybotApproval,
  KrybotApprovalSummary,
  KrybotApprovalDecision,
  KrybotApprovalDecisionRequest,
  KrybotApprovalDecisionResponse,
  KrybotApprovalAssignmentRequest,
  KrybotApprovalListResponse,
  KrybotApprovalHistoryResponse,
  KrybotTemplateVersion,
  KrybotTemplate,
  KrybotTemplateCreate,
  KrybotTemplateUpdate,
  KrybotTemplateVersionCreate,
  KrybotTemplateRenderRequest,
  KrybotTemplateRenderResponse,
  ChatBootstrapRequest,
  ChatBootstrapResponse,
  ChatTurnRequest,
  ChatTurnResponse,
  ChatHistoryResponse,
  ChatAttachmentUploadResponse,
  PersonDeliverRequest,
  PersonDeliverResponse,
  RouteExtractionResponse,
  ChatThreadResponse,
  ThreadAgentAssignApiResponse,
  AgentHandoffRequest,
  ThreadContextRefreshResponse,
  AgentType,
  KrybotOrchestrationCreate,
  KrybotOrchestrationGraph,
  KrybotOrchestrationRecord,
  KrybotOrchestrationStatus,
  KrybotInvokeEdgeCreate,
  KrybotSpawnChildRunPayload,
  KrybotSpawnChildRunResponse,
} from '../../types/krybot';
import { normalizeThreadAgentAssignResponse } from '../../types/krybot';
import {
  inferAgentModeFromUiRole,
  SERVER_THREAD_CONTEXT_VERSION,
  type AgentMode,
} from '../../types/threadContext';
import { API_ENDPOINTS } from './config';
import type { KrybotAgentId } from '../../catalog/krysalisBridgeCatalog';
import { krybotSkillBundle } from '../../catalog/krysalisBridgeCatalog';

export interface KrybotSkillContentResponse {
  skill_bundle: string;
  paths: string[];
  content: string;
  truncated: boolean;
}

/**
 * All skill bundle slugs for an agent (explicit + bridge-resolved), deduped.
 * Order matches backend `_skill_bundle_slugs_from_agent`: per blob, `skill_bundle` then
 * `bridge_krybot_agent_id`; `capabilities` then `default_context`.
 */
export function resolveAgentSkillBundles(agent: {
  capabilities?: Record<string, unknown>;
  default_context?: Record<string, unknown>;
}): string[] {
  const out: string[] = [];
  const seen = new Set<string>();

  const push = (slug: string) => {
    if (seen.has(slug)) {
      return;
    }
    seen.add(slug);
    out.push(slug);
  };

  for (const blob of [agent.capabilities, agent.default_context]) {
    if (!blob || typeof blob !== 'object') {
      continue;
    }
    const sb = blob.skill_bundle;
    if (typeof sb === 'string' && sb.trim()) {
      push(sb.trim());
    }
    const brid = blob.bridge_krybot_agent_id;
    if (typeof brid === 'string' && brid.trim()) {
      try {
        push(krybotSkillBundle(brid.trim() as KrybotAgentId));
      } catch {
        /* invalid or unknown bridge id */
      }
    }
  }
  return out;
}

/** First skill bundle slug, or null — convenience when only one value is needed. */
export function resolveAgentSkillBundle(agent: {
  capabilities?: Record<string, unknown>;
  default_context?: Record<string, unknown>;
}): string | null {
  const all = resolveAgentSkillBundles(agent);
  return all[0] ?? null;
}

export class KrybotService {
  private static readonly BASE_PATH = '/krybot';

  // Agent Management
  static async listAgents(params?: {
    is_active?: boolean;
    agent_type?: AgentType;
  }): Promise<KrybotAgent[]> {
    const queryParams = new URLSearchParams();
    if (params?.is_active !== undefined) {
      queryParams.append('is_active', params.is_active.toString());
    }
    if (params?.agent_type) {
      queryParams.append('agent_type', params.agent_type);
    }
    const queryString = queryParams.toString();
    const url = `${this.BASE_PATH}/agents${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get<KrybotAgent[]>(url);
    return response.data;
  }

  static async getAgent(agentId: string): Promise<KrybotAgent> {
    const response = await apiClient.get<KrybotAgent>(`${this.BASE_PATH}/agents/${agentId}`);
    return response.data;
  }

  /** Full SKILL markdown from repo (same sources as LLM system prompt materials). */
  static async getSkillContent(skillBundle: string): Promise<KrybotSkillContentResponse> {
    const url = API_ENDPOINTS.KRYBOT.SKILL_CONTENT(skillBundle);
    const response = await apiClient.get<KrybotSkillContentResponse>(url);
    return response.data;
  }

  static async createAgent(payload: KrybotAgentCreate): Promise<KrybotAgent> {
    const response = await apiClient.post<KrybotAgent>(`${this.BASE_PATH}/agents`, payload);
    return response.data;
  }

  static async updateAgent(agentId: string, payload: KrybotAgentUpdate): Promise<KrybotAgent> {
    const response = await apiClient.patch<KrybotAgent>(`${this.BASE_PATH}/agents/${agentId}`, payload);
    return response.data;
  }

  static async createAgentVersion(
    agentId: string,
    payload: KrybotAgentVersionCreate
  ): Promise<KrybotAgentVersion> {
    const response = await apiClient.post<KrybotAgentVersion>(
      `${this.BASE_PATH}/agents/${agentId}/versions`,
      payload
    );
    return response.data;
  }

  static async activateAgentVersion(agentId: string, versionTag: string): Promise<KrybotAgent> {
    const response = await apiClient.post<KrybotAgent>(
      `${this.BASE_PATH}/agents/${agentId}/activate?version_tag=${versionTag}`
    );
    return response.data;
  }

  // Agent Runs
  static async startAgentRun(agentId: string, payload: KrybotAgentRunCreate): Promise<KrybotAgentRun> {
    const response = await apiClient.post<KrybotAgentRun>(
      `${this.BASE_PATH}/agents/${agentId}/runs`,
      payload
    );
    return response.data;
  }

  static async listAgentRuns(params?: {
    status_filter?: string;
    thread_id?: string;
    artifact_scope?: string;
    approval_state?: string;
  }): Promise<KrybotAgentRunStatus[]> {
    const queryParams = new URLSearchParams();
    if (params?.status_filter) {
      queryParams.append('status_filter', params.status_filter);
    }
    if (params?.thread_id) {
      queryParams.append('thread_id', params.thread_id);
    }
    if (params?.artifact_scope) {
      queryParams.append('artifact_scope', params.artifact_scope);
    }
    if (params?.approval_state) {
      queryParams.append('approval_state', params.approval_state);
    }
    const queryString = queryParams.toString();
    const url = `${this.BASE_PATH}/agents/runs${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get<KrybotAgentRunStatus[]>(url);
    return response.data;
  }

  static async getAgentRun(runId: string): Promise<KrybotAgentRun> {
    const response = await apiClient.get<KrybotAgentRun>(`${this.BASE_PATH}/agents/runs/${runId}`);
    return response.data;
  }

  static async cancelAgentRun(runId: string): Promise<KrybotAgentRun> {
    const response = await apiClient.post<KrybotAgentRun>(
      `${this.BASE_PATH}/agents/runs/${runId}/cancel`
    );
    return response.data;
  }

  static async respondToRun(runId: string, feedback: string): Promise<KrybotAgentRun> {
    const response = await apiClient.post<KrybotAgentRun>(
      `${this.BASE_PATH}/agents/runs/${runId}/respond`,
      { feedback }
    );
    return response.data;
  }

  static async resubmitRun(
    runId: string, 
    additionalInstructions: string, 
    includeOriginalInstruction: boolean = true
  ): Promise<KrybotAgentRun> {
    const response = await apiClient.post<KrybotAgentRun>(
      `${this.BASE_PATH}/agents/runs/${runId}/resubmit`,
      { 
        additional_instructions: additionalInstructions,
        include_original_instruction: includeOriginalInstruction
      }
    );
    return response.data;
  }

  // Chat Operations
  /**
   * Server-side resume of the latest Krybot thread for user + agent_mode (+ optional company).
   * Used by Discovery so closing the browser does not mint an empty prospector session.
   */
  static async resumeChatThread(params: {
    userId: string;
    agentMode?: string;
    companyId?: string;
  }): Promise<{ resumed: boolean; thread: ChatThreadResponse | null }> {
    const response = await apiClient.get<{
      resumed?: boolean;
      thread?: ChatThreadResponse | null;
    }>(`${this.BASE_PATH}/chat/threads/resume`, {
      params: {
        user_id: params.userId,
        agent_mode: params.agentMode ?? 'prospector',
        ...(params.companyId ? { company_id: params.companyId } : {}),
      },
    });
    const data = response.data ?? {};
    return {
      resumed: Boolean(data.resumed && data.thread?.id),
      thread: data.thread ?? null,
    };
  }

  /**
   * ISSUE-243: Every bootstrap sends `agent_mode` (explicit or inferred from `ui_role`)
   * and `context` with at least `context_version` so Krybot and REST stay aligned.
   */
  static async bootstrapThread(payload: ChatBootstrapRequest): Promise<ChatBootstrapResponse> {
    const ctx =
      payload.context &&
      typeof payload.context === 'object' &&
      !Array.isArray(payload.context)
        ? { ...(payload.context as Record<string, unknown>) }
        : {};
    const agentMode = (payload.agent_mode ?? inferAgentModeFromUiRole(payload.ui_role)) as AgentMode;
    const ctxModeRaw =
      (typeof ctx.agent_mode === 'string' && ctx.agent_mode) ||
      (typeof ctx.agentMode === 'string' && ctx.agentMode) ||
      null;
    const contextAgentMode = (ctxModeRaw || agentMode) as AgentMode;
    const merged: ChatBootstrapRequest = {
      ...payload,
      agent_mode: agentMode,
      context: {
        context_version: SERVER_THREAD_CONTEXT_VERSION,
        ...ctx,
        // Ensure agent_mode is always present in context (ctx may omit it)
        agent_mode: contextAgentMode,
      },
    };
    const response = await apiClient.post<ChatBootstrapResponse>(
      `${this.BASE_PATH}/chat/threads/bootstrap`,
      merged
    );
    return response.data;
  }

  /**
   * List Krybot chat threads with hygiene markers (`is_empty`, `is_default_named`, `message_count`).
   * Prefer this over legacy ThreadService when markers are needed.
   */
  static async listChatThreads(
    userId: string,
    options?: { emptyOnly?: boolean; companyId?: string }
  ): Promise<ChatThreadResponse[]> {
    const response = await apiClient.get<
      ChatThreadResponse[] | { threads?: ChatThreadResponse[]; data?: ChatThreadResponse[] }
    >(`${this.BASE_PATH}/chat/threads`, {
      params: {
        user_id: userId,
        ...(options?.emptyOnly ? { empty_only: true } : {}),
        ...(options?.companyId ? { company_id: options.companyId } : {}),
      },
    });
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') {
      if (Array.isArray(data.threads)) return data.threads;
      if (Array.isArray(data.data)) return data.data;
    }
    return [];
  }

  /** Soft-archive empty drafts past TTL; keeps one reusable draft by default. */
  static async sweepEmptyChatThreads(payload: {
    user_id: string;
    older_than_hours?: number;
    keep_one_draft?: boolean;
  }): Promise<{ archived_count?: number; kept_draft_id?: string | null }> {
    const response = await apiClient.post<{
      archived_count?: number;
      kept_draft_id?: string | null;
    }>(`${this.BASE_PATH}/chat/threads/sweep-empty`, {
      older_than_hours: 24,
      keep_one_draft: true,
      ...payload,
    });
    return response.data ?? {};
  }

  static async sendMessage(threadId: string, payload: ChatTurnRequest): Promise<ChatTurnResponse> {
    const response = await apiClient.post<ChatTurnResponse>(
      `${this.BASE_PATH}/chat/threads/${threadId}/messages`,
      payload
    );
    return response.data;
  }

  // Alias for processChatTurn (same as sendMessage)
  static async processChatTurn(threadId: string, payload: ChatTurnRequest): Promise<ChatTurnResponse> {
    return this.sendMessage(threadId, payload);
  }

  static async getChatHistory(
    threadId: string,
    options?: { userId?: string }
  ): Promise<ChatHistoryResponse> {
    const params =
      options?.userId && options.userId.trim()
        ? { user_id: options.userId.trim() }
        : undefined;
    const response = await apiClient.get<ChatHistoryResponse>(
      `${this.BASE_PATH}/chat/threads/${threadId}/history`,
      { params }
    );
    return response.data;
  }

  static async assignAgent(threadId: string, agentId: string): Promise<ChatThreadResponse> {
    const response = await apiClient.patch<ThreadAgentAssignApiResponse>(
      `${this.BASE_PATH}/chat/threads/${threadId}/agent`,
      { agent_id: agentId }
    );
    return normalizeThreadAgentAssignResponse(response.data);
  }

  static async handoffAgent(threadId: string, payload: AgentHandoffRequest): Promise<ChatTurnResponse> {
    const response = await apiClient.post<ChatTurnResponse>(
      `${this.BASE_PATH}/chat/threads/${threadId}/handoff`,
      payload
    );
    return response.data;
  }

  static async refreshContext(threadId: string): Promise<ThreadContextRefreshResponse> {
    const response = await apiClient.post<ThreadContextRefreshResponse>(
      `${this.BASE_PATH}/chat/threads/${threadId}/refresh-context`
    );
    return response.data;
  }

  static async uploadThreadAttachment(
    threadId: string,
    file: File,
    options?: { userId: string; artifactItemRef?: string }
  ): Promise<ChatAttachmentUploadResponse> {
    if (!options?.userId) {
      throw new Error('userId is required to upload a chat attachment');
    }
    const form = new FormData();
    form.append('file', file);
    form.append('user_id', options.userId);
    if (options.artifactItemRef) {
      form.append('artifact_item_ref', options.artifactItemRef);
    }
    const response = await apiClient.post<ChatAttachmentUploadResponse>(
      API_ENDPOINTS.KRYBOT.CHAT.ATTACHMENTS(threadId),
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  static async deliverPersonMessage(
    threadId: string,
    payload: PersonDeliverRequest
  ): Promise<PersonDeliverResponse> {
    const response = await apiClient.post<PersonDeliverResponse>(
      API_ENDPOINTS.KRYBOT.CHAT.PERSON_DELIVER(threadId),
      payload
    );
    return response.data;
  }

  static async routeAttachmentExtraction(
    threadId: string,
    attachmentId: string,
    options: { userId: string; artifactItemRef?: string }
  ): Promise<RouteExtractionResponse> {
    const response = await apiClient.post<RouteExtractionResponse>(
      API_ENDPOINTS.KRYBOT.CHAT.ATTACHMENT_ROUTE_EXTRACTION(threadId, attachmentId),
      {
        user_id: options.userId,
        artifact_item_ref: options.artifactItemRef,
      }
    );
    return response.data;
  }

  /**
   * Find the MarketFit_agent (case-insensitive)
   * Used for MarketFit guided flow and situation/product chat
   */
  static async findMarketFitAgent(): Promise<KrybotAgent | null> {
    try {
      const agents = await this.listAgents({ agent_type: 'chat', is_active: true });
      const marketFitAgent = agents.find(
        (agent) => agent.name && agent.name.toLowerCase() === 'marketfit_agent'
      );
      return marketFitAgent ?? null;
    } catch (error) {
      console.error('Failed to find MarketFit agent:', error);
      return null;
    }
  }

  /**
   * Ordered agent names to try per ISSUE-243 / backend `thread_agent_resolver.AGENT_NAME_FALLBACKS`.
   * Used when assigning an agent to a thread (e.g. ensureChatAgentAssigned).
   */
  private static readonly _CHAT_AGENT_NAME_PRIORITY: Record<
    AgentMode,
    readonly string[]
  > = {
    prospector: ['Prospector_Chat', 'Prospector', 'Krybot_Chat'],
    company_agent: ['Company_Agent_Chat', 'Company_Agent', 'Krybot_Chat'],
    bridge_session: ['Bridge_Chat', 'Bridge_Session_Chat', 'Krybot_Chat'],
    generic: ['Krybot_Chat'],
  };

  /**
   * Pick a chat agent for assignment, aligned with `agent_mode` from `ui_role`
   * (same order as backend `resolve_chat_agent_id`).
   */
  static async findChatAgent(uiRole?: string): Promise<KrybotAgent | null> {
    try {
      const agents = await this.listAgents({ agent_type: 'chat', is_active: true });
      if (agents.length === 0) {
        return null;
      }

      const mode = inferAgentModeFromUiRole(uiRole);
      const priority = KrybotService._CHAT_AGENT_NAME_PRIORITY[mode];

      for (const name of priority) {
        const found = agents.find(
          (agent) => agent.name && agent.name.toLowerCase() === name.toLowerCase()
        );
        if (found) {
          return found;
        }
      }

      return agents[0];
    } catch (error) {
      console.error('Failed to find chat agent:', error);
      return null;
    }
  }

  /**
   * Ensure a chat agent is assigned to a thread.
   * Picks agent via `findChatAgent` using `thread.ui_role` / `uiRoleForBootstrap` (Prospector vs operator, etc.).
   * Falls back to bootstrap with `preferred_agent_id` if PATCH is unavailable.
   */
  static async ensureChatAgentAssigned(
    threadId: string,
    thread?: { active_agent_id?: string | null; ui_role?: string | null },
    userId?: string,
    uiRoleForBootstrap: string = 'operator'
  ): Promise<boolean> {
    try {
      // Check if thread already has an agent
      if (thread?.active_agent_id) {
        return true;
      }

      const uiRoleForFind =
        (typeof thread?.ui_role === 'string' && thread.ui_role.trim()
          ? thread.ui_role
          : undefined) ||
        uiRoleForBootstrap ||
        'operator';

      const chatAgent = await this.findChatAgent(uiRoleForFind);
      if (!chatAgent) {
        console.warn('No chat agent found to assign to thread', threadId);
        return false;
      }
      
      // Try to assign the agent using PATCH endpoint
      try {
        await this.assignAgent(threadId, chatAgent.id);
        console.log(`✅ Assigned chat agent ${chatAgent.name} (${chatAgent.id}) to thread ${threadId}`);
        return true;
      } catch (patchError: any) {
        // PATCH unavailable or rejected — try bootstrap with preferred_agent_id
        const patchStatus = patchError?.response?.status;
        if ((patchStatus === 404 || patchStatus === 501 || patchStatus === 422) && userId) {
          console.log('PATCH agent assignment failed, trying bootstrap with preferred_agent_id...', patchStatus);
          try {
            const uiRole =
              uiRoleForBootstrap ||
              (typeof thread?.ui_role === 'string' && thread.ui_role ? thread.ui_role : undefined) ||
              'operator';
            // Bootstrap with preferred_agent_id to assign the agent
            await this.bootstrapThread({
              user_id: userId,
              thread_id: threadId,
              preferred_agent_id: chatAgent.id,
              ui_role: uiRole,
            });
            console.log(`✅ Assigned chat agent via bootstrap: ${chatAgent.name} (${chatAgent.id})`);
            return true;
          } catch (bootstrapError) {
            console.warn('Bootstrap with preferred_agent_id also failed:', bootstrapError);
            return false;
          }
        }
        // If no userId provided or other error, just log
        if (!userId) {
          console.warn('Cannot assign agent: PATCH endpoint unavailable and no userId provided for bootstrap fallback');
        } else {
          console.error('Failed to assign agent via PATCH:', patchError);
        }
        return false;
      }
    } catch (error) {
      console.error('Failed to ensure chat agent assigned:', error);
      return false;
    }
  }

  // Approvals
  static async listApprovals(params?: {
    status_filter?: string;
    agent_id?: string;
  }): Promise<KrybotApprovalListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.status_filter) {
      queryParams.append('status_filter', params.status_filter);
    }
    if (params?.agent_id) {
      queryParams.append('agent_id', params.agent_id);
    }
    const queryString = queryParams.toString();
    const url = `${this.BASE_PATH}/approvals${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get<KrybotApprovalListResponse>(url);
    return response.data;
  }

  static async getApproval(approvalId: string): Promise<KrybotApproval> {
    const response = await apiClient.get<KrybotApproval>(`${this.BASE_PATH}/approvals/${approvalId}`);
    return response.data;
  }

  static async makeDecision(
    approvalId: string,
    payload: KrybotApprovalDecisionRequest
  ): Promise<KrybotApprovalDecisionResponse> {
    const response = await apiClient.post<KrybotApprovalDecisionResponse>(
      `${this.BASE_PATH}/approvals/${approvalId}/decision`,
      payload
    );
    return response.data;
  }

  static async assignApproval(
    approvalId: string,
    payload: KrybotApprovalAssignmentRequest
  ): Promise<KrybotApproval> {
    const response = await apiClient.post<KrybotApproval>(
      `${this.BASE_PATH}/approvals/${approvalId}/assign`,
      payload
    );
    return response.data;
  }

  static async getApprovalHistory(approvalId: string): Promise<KrybotApprovalHistoryResponse> {
    const response = await apiClient.get<KrybotApprovalHistoryResponse>(
      `${this.BASE_PATH}/approvals/${approvalId}/history`
    );
    return response.data;
  }

  // Templates
  static async listTemplates(): Promise<KrybotTemplate[]> {
    const response = await apiClient.get<KrybotTemplate[]>(`${this.BASE_PATH}/templates`);
    return response.data;
  }

  static async getTemplate(templateKey: string): Promise<KrybotTemplate> {
    const response = await apiClient.get<KrybotTemplate>(`${this.BASE_PATH}/templates/${templateKey}`);
    return response.data;
  }

  static async createTemplate(payload: KrybotTemplateCreate): Promise<KrybotTemplate> {
    const response = await apiClient.post<KrybotTemplate>(`${this.BASE_PATH}/templates`, payload);
    return response.data;
  }

  static async updateTemplate(
    templateKey: string,
    payload: KrybotTemplateUpdate
  ): Promise<KrybotTemplate> {
    const response = await apiClient.patch<KrybotTemplate>(
      `${this.BASE_PATH}/templates/${templateKey}`,
      payload
    );
    return response.data;
  }

  static async createTemplateVersion(
    templateKey: string,
    payload: KrybotTemplateVersionCreate
  ): Promise<KrybotTemplateVersion> {
    const response = await apiClient.post<KrybotTemplateVersion>(
      `${this.BASE_PATH}/templates/${templateKey}/versions`,
      payload
    );
    return response.data;
  }

  static async renderTemplate(
    payload: KrybotTemplateRenderRequest
  ): Promise<KrybotTemplateRenderResponse> {
    const response = await apiClient.post<KrybotTemplateRenderResponse>(
      `${this.BASE_PATH}/templates/${payload.template_key}/render`,
      payload
    );
    return response.data;
  }

  // Run orchestration (DAG / invokes; separate from single-run dispatch)
  static async createOrchestration(
    payload: KrybotOrchestrationCreate
  ): Promise<KrybotOrchestrationRecord> {
    const response = await apiClient.post<KrybotOrchestrationRecord>(
      `${this.BASE_PATH}/orchestration`,
      payload
    );
    return response.data;
  }

  static async getOrchestrationGraph(orchestrationId: string): Promise<KrybotOrchestrationGraph> {
    const response = await apiClient.get<KrybotOrchestrationGraph>(
      `${this.BASE_PATH}/orchestration/${orchestrationId}`
    );
    return response.data;
  }

  static async listWorktreeInvokes(agentId: string): Promise<string[]> {
    const response = await apiClient.get<string[]>(
      `${this.BASE_PATH}/orchestration/worktree/agents/${encodeURIComponent(agentId)}/invokes`
    );
    return response.data;
  }

  static async listWorktreeEdges(): Promise<Array<{ from: string; to: string }>> {
    const response = await apiClient.get<Array<{ from: string; to: string }>>(
      `${this.BASE_PATH}/orchestration/worktree/edges`
    );
    return response.data;
  }

  static async patchOrchestrationStatus(
    orchestrationId: string,
    status: KrybotOrchestrationStatus
  ): Promise<{ ok: boolean }> {
    const response = await apiClient.patch<{ ok: boolean }>(
      `${this.BASE_PATH}/orchestration/${orchestrationId}/status`,
      { status }
    );
    return response.data;
  }

  static async patchOrchestrationRootRun(
    orchestrationId: string,
    rootRunId: string
  ): Promise<{ ok: boolean }> {
    const response = await apiClient.patch<{ ok: boolean }>(
      `${this.BASE_PATH}/orchestration/${orchestrationId}/root-run`,
      { root_run_id: rootRunId }
    );
    return response.data;
  }

  static async postOrchestrationInvokeEdge(
    orchestrationId: string,
    payload: KrybotInvokeEdgeCreate
  ): Promise<{ id: string; orchestration_id: string }> {
    const response = await apiClient.post<{ id: string; orchestration_id: string }>(
      `${this.BASE_PATH}/orchestration/${orchestrationId}/edges`,
      payload
    );
    return response.data;
  }

  static async attachRunToOrchestration(
    orchestrationId: string,
    runId: string
  ): Promise<{ ok: boolean }> {
    const response = await apiClient.post<{ ok: boolean }>(
      `${this.BASE_PATH}/orchestration/${orchestrationId}/attach-run`,
      { run_id: runId }
    );
    return response.data;
  }

  static async spawnOrchestrationChildRun(
    orchestrationId: string,
    payload: KrybotSpawnChildRunPayload
  ): Promise<KrybotSpawnChildRunResponse> {
    const response = await apiClient.post<KrybotSpawnChildRunResponse>(
      `${this.BASE_PATH}/orchestration/${orchestrationId}/spawn-run`,
      payload
    );
    return response.data;
  }

  // Prompt Builder
  static async promptBuilderChat(payload: {
    messages: Array<{ role: 'user' | 'assistant' | 'system'; content: string }>;
    model?: string;
    temperature?: number;
    agent_context?: {
      name?: string;
      persona_prompt?: string;
    };
  }): Promise<{ message: string; role: string; mapping?: { raw?: string }; model?: string }> {
    const response = await apiClient.post<{ message: string; role: string; mapping?: { raw?: string }; model?: string }>(
      `${this.BASE_PATH}/chat/prompt-builder`,
      payload
    );
    return response.data;
  }
}

